<template>
  <ModernLogin 
    v-if="currentTheme === 'modern'"
    v-model:userName="userName"
    v-model:password="password"
    v-model:organizationName="organizationName"
    v-model:otpDigits="otpDigits"
    :errorMessage="errorMessage"
    :isLoading="isLoading"
    :isOtpLoading="isOtpLoading"
    :mfaStep="mfaStep"
    :authAppSetupStep="authAppSetupStep"
    :safeAuthAppQrCode="safeAuthAppQrCode"
    :authAppSecret="authAppSecret"
    :mfaTitle="mfaTitle"
    :mfaDescription="mfaDescription"
    :mfaInputLabel="mfaInputLabel"
    :mfaButtonLabel="mfaButtonLabel"
    :isRTL="isRTL"
    @login="login"
    @verifyOtp="submitMfaCode"
    @returnToLogin="returnToLogin"
  />
  
  <div v-else id="loginPage" dir="ltr" class="min-h-screen h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex">
    <div id="loginLeftPanel" :dir="isRTL === true ? 'rtl' : 'ltr'" class="enterprise-visual-panel order-2 hidden p-8 lg:flex lg:w-[46%] lg:p-12 xl:p-16 flex-col justify-between relative overflow-hidden shadow-2xl">
      <div id="loginGlobe" aria-hidden="true">
        <svg viewBox="0 0 640 640" role="presentation">
          <defs>
            <radialGradient id="globeSurface" cx="34%" cy="27%" r="72%">
              <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.16" />
              <stop offset="48%" stop-color="#2563eb" stop-opacity="0.07" />
              <stop offset="100%" stop-color="#020617" stop-opacity="0.02" />
            </radialGradient>
            <linearGradient id="globeLine" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#bae6fd" stop-opacity="0.66" />
              <stop offset="48%" stop-color="#38bdf8" stop-opacity="0.3" />
              <stop offset="100%" stop-color="#2563eb" stop-opacity="0.08" />
            </linearGradient>
            <radialGradient id="globeEdge" cx="38%" cy="33%" r="70%">
              <stop offset="62%" stop-color="#38bdf8" stop-opacity="0" />
              <stop offset="91%" stop-color="#38bdf8" stop-opacity="0.12" />
              <stop offset="100%" stop-color="#7dd3fc" stop-opacity="0.42" />
            </radialGradient>
            <filter id="globeGlow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="10" />
            </filter>
            <clipPath id="globeClip">
              <circle cx="320" cy="320" r="246" />
            </clipPath>
          </defs>
          <circle class="globe-glow" cx="320" cy="320" r="256" />
          <circle cx="320" cy="320" r="246" fill="url(#globeSurface)" />
          <g class="globe-rotating-content">
            <g class="globe-grid" clip-path="url(#globeClip)" fill="none" stroke="url(#globeLine)">
              <ellipse cx="320" cy="320" rx="82" ry="246" />
              <ellipse cx="320" cy="320" rx="162" ry="246" />
              <ellipse cx="320" cy="320" rx="246" ry="74" />
              <ellipse cx="320" cy="320" rx="246" ry="151" />
              <path d="M74 320H566" />
              <path d="M103 204C170 241 246 260 320 260C394 260 470 241 537 204" />
              <path d="M103 436C170 399 246 380 320 380C394 380 470 399 537 436" />
            </g>
            <g class="globe-network" clip-path="url(#globeClip)" fill="none">
              <path d="M154 221L236 171L331 236L419 190L507 277L442 358L519 418" />
              <path d="M118 356L213 302L301 349L382 293L470 337L536 287" />
              <path d="M186 461L246 390L349 437L442 358" />
              <g class="globe-points">
                <circle cx="154" cy="221" r="4" />
                <circle cx="236" cy="171" r="3" />
                <circle cx="331" cy="236" r="5" />
                <circle cx="419" cy="190" r="3" />
                <circle cx="507" cy="277" r="4" />
                <circle cx="442" cy="358" r="5" />
                <circle cx="519" cy="418" r="3" />
                <circle cx="118" cy="356" r="3" />
                <circle cx="213" cy="302" r="4" />
                <circle cx="301" cy="349" r="3" />
                <circle cx="382" cy="293" r="4" />
                <circle cx="470" cy="337" r="3" />
                <circle cx="186" cy="461" r="4" />
                <circle cx="246" cy="390" r="3" />
                <circle cx="349" cy="437" r="4" />
              </g>
            </g>
          </g>
          <circle cx="320" cy="320" r="246" fill="url(#globeEdge)" />
          <path class="globe-highlight" d="M155 179C197 127 257 96 320 87" />
        </svg>
      </div>

      <div id="loginLeftContent" class="relative z-10 flex-1 flex flex-col justify-center max-w-xl">
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
          {{ t('marketing.enterpriseIntelligence', 'Enterprise intelligence') }}
        </p>
        <h2 class="mt-5 text-[2.65rem] xl:text-5xl font-semibold text-white leading-[1.08] tracking-[-0.025em]">
          {{ t('marketing.enterpriseHeadline', 'AI operations built for real business.') }}
        </h2>
        <p class="mt-6 text-base xl:text-lg leading-7 xl:leading-8 text-slate-300">
          {{ t('marketing.enterpriseDescription', 'Deploy governed AI agents across your organization with the control, security, and visibility enterprise teams expect.') }}
        </p>

        <div class="mt-10 space-y-4">
          <div class="rounded-xl border border-white/10 bg-slate-950/25 p-5 shadow-sm">
            <p class="text-sm font-semibold text-white">{{ t('marketing.enterpriseKnowledgeTitle', 'Connect your knowledge') }}</p>
            <p class="mt-2 text-sm leading-6 text-slate-400">{{ t('marketing.enterpriseKnowledgeDescription', 'Bring together trusted data, documents, and business systems.') }}</p>
          </div>
          <div class="rounded-xl border border-white/10 bg-slate-950/25 p-5 shadow-sm">
            <p class="text-sm font-semibold text-white">{{ t('marketing.enterpriseConfidenceTitle', 'Operate with confidence') }}</p>
            <p class="mt-2 text-sm leading-6 text-slate-400">{{ t('marketing.enterpriseConfidenceDescription', 'Apply guardrails, permissions, analytics, and human oversight.') }}</p>
          </div>
          <div class="rounded-xl border border-white/10 bg-slate-950/25 p-5 shadow-sm">
            <p class="text-sm font-semibold text-white">{{ t('marketing.enterpriseScaleTitle', 'Scale across every channel') }}</p>
            <p class="mt-2 text-sm leading-6 text-slate-400">{{ t('marketing.enterpriseScaleDescription', 'Launch consistent experiences wherever your teams and customers work.') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div id="loginRightPanel" :dir="isRTL === true ? 'rtl' : 'ltr'" class="order-1 flex-1 lg:w-[54%] flex items-center justify-center p-6 sm:p-8 lg:p-12 xl:p-16 bg-white dark:bg-slate-900 relative">
      <div id="loginRightPanelOverlay" class="absolute inset-0 bg-gradient-to-br from-slate-50/30 via-transparent to-slate-100/30 dark:from-slate-800/30 dark:via-transparent dark:to-slate-700/30"></div>

      <div id="loginFormContainer" class="w-full max-w-md lg:max-w-lg xl:max-w-xl space-y-8 lg:space-y-8 relative z-10">
        <div id="loginMobileHeader" class="lg:hidden flex items-center justify-center space-x-4 mb-8 lg:mb-12">
          <div id="loginMobileLogo" class="h-12 w-12 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center shadow-lg">
            <Icon icon="mingcute:ai-line" class="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div id="loginMobileText">
            <h1 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight">
              {{ brandName }}
            </h1>
            <p class="text-slate-500 text-xs sm:text-sm font-medium">Enterprise Platform</p>
          </div>
        </div>

        <div id="loginWelcomeMessage" class="text-center space-y-6">
          <h2 class="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            {{ t('login.welcomeTo', 'Welcome to') }}
          </h2>
          <p class="text-slate-600 dark:text-slate-400 text-base sm:text-lg">
            {{ t('login.enterCredentials', 'Please enter your credentials to continue.') }}
          </p>
        </div>

        <button type="button" id="azureLoginButton" class="w-full flex items-center justify-center rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300 shadow-sm hover:shadow-md">
          <span class="mr-4 ml-1" style="margin-right: 2%;">{{ t('login.signInWithAzure') }}</span>
          <Icon icon="devicon:azure" class="w-6 h-6 mr-4 text-blue-600" />
        </button>

        <div id="loginDividerWrapper" class="relative">
          <div id="loginDividerLineWrapper" class="absolute inset-0 flex items-center">
            <div id="loginDividerLine" class="w-full border-t border-slate-200 dark:border-slate-700"></div>
          </div>
          <div id="loginDividerLabel" class="relative flex justify-center text-sm">
            <span class="px-4 bg-white dark:bg-slate-900 text-slate-500 font-medium">{{ t('login.or') }}</span>
          </div>
        </div>

        <div id="authAppSetupSection" v-if="authAppSetupStep === true" class="space-y-6 lg:space-y-8">
          <div class="text-center space-y-4">
            <div class="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 mb-2">
              <svg class="w-7 h-7 sm:w-8 sm:h-8 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c.5304 0 1.0391-.2107 1.4142-.5858C13.7893 10.0391 14 9.5304 14 9c0-.53043-.2107-1.03914-.5858-1.41421C13.0391 7.21071 12.5304 7 12 7c-.5304 0-1.0391.21071-1.4142.58579C10.2107 7.96086 10 8.46957 10 9c0 .5304.2107 1.0391.5858 1.4142C10.9609 10.7893 11.4696 11 12 11zM19 21a7 7 0 10-14 0z" />
              </svg>
            </div>
            <div>
              <h3 class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{{ t('login.authAppSetupTitle', 'Set up your authenticator app') }}</h3>
              <p class="text-slate-600 dark:text-slate-400 text-base sm:text-lg mt-2">
                {{ t('login.authAppSetupDescription', 'Scan the QR code with your authenticator app, then return to log in again.') }}
              </p>
            </div>
          </div>
          <div class="flex flex-col items-center space-y-4">
            <img
              v-if="safeAuthAppQrCode !== ''"
              :src="safeAuthAppQrCode"
              alt="Authenticator QR code"
              class="w-48 h-48 sm:w-56 sm:h-56 border-2 border-slate-200 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-900 p-3 shadow-lg"
            />
            <div class="w-full">
              <p class="text-center text-sm text-slate-500 dark:text-slate-400">{{ t('login.manualCodeLabel', 'Or enter this code manually') }}</p>
              <div class="mt-2 px-4 py-3 border-2 border-slate-200 dark:border-slate-700 rounded-lg sm:rounded-xl font-mono text-base tracking-widest text-slate-900 dark:text-white text-center break-all select-all">{{ authAppSecret }}</div>
            </div>
          </div>
          <button
            @click="returnToLogin"
            class="w-full bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white font-bold py-3 sm:py-4 px-6 rounded-lg sm:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {{ t('login.backToLogin', 'Back to login') }}
          </button>
        </div>

        <template v-else>
        <div id="credentialsSection" class="space-y-6 lg:space-y-8" v-if="mfaStep === false">
          <div id="loginCredentialInputs" class="space-y-5 lg:space-y-6">
            <div id="organizationNameInput" class="space-y-2">
              <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wide">
                {{ t('login.organizationName') }}
              </label>
              <div id="loginOrganizationInputWrapper" class="relative">
                <input
                  type="text"
                  v-model="organizationName"
                  class="w-full px-4 py-3 sm:py-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg sm:rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 text-sm sm:text-base"
                  :dir="isRTL === true ? 'rtl' : 'ltr'"
                  :placeholder="t('login.organizationNamePlaceholder', 'Enter your organization name')"
                />
                <div id="loginOrganizationInputOverlay" class="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none"></div>
              </div>
            </div>

            <div id="usernameInputBox" class="space-y-2">
              <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wide">
                {{ t('login.username') }}
              </label>
              <div id="loginUsernameInputWrapper" class="relative">
                <input
                  type="text"
                  v-model="userName"
                  @blur="trimUserName"
                  class="w-full px-4 py-3 sm:py-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg sm:rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 text-sm sm:text-base"
                  :placeholder="t('login.usernamePlaceholder', 'Enter your username')"
                />
                <div id="loginUsernameInputOverlay" class="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none"></div>
              </div>
            </div>

            <div id="passwordInputBox" class="space-y-2">
              <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wide">
                {{ t('login.password') }}
              </label>
              <div id="loginPasswordInputWrapper" class="relative">
                <input
                  type="password"
                  v-model="password"
                  @blur="trimPassword"
                  class="w-full px-4 py-3 sm:py-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg sm:rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 text-sm sm:text-base"
                  :placeholder="t('login.passwordPlaceholder', 'Enter your password')"
                />
                <div id="loginPasswordInputOverlay" class="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none"></div>
              </div>
            </div>
          </div>

          <div id="loginCredentialsError" v-if="errorMessage!==''" class="text-red-600 dark:text-red-400 text-sm font-medium text-center bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-200 dark:border-red-800">
            <div id="loginCredentialsErrorContent" class="flex items-center justify-center space-x-2">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <span>{{ errorMessage }}</span>
            </div>
          </div>

          <button
            @click="login"
            class="w-full bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white font-bold py-3 sm:py-4 px-6 rounded-lg sm:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <span class="flex items-center justify-center space-x-3">
              <span class="mr-2 ml-2" style="margin-right: 2%;">{{ t('login.loginButton') }}</span>
              <svg class="w-4 h-4 mr-2 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20" style="margin-right:2%;">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </span>
          </button>
        </div>

        <div id="mfaSection" v-if="mfaStep === true" class="space-y-6 lg:space-y-8">
          <div id="loginMfaHeader" class="text-center space-y-4">
            <div id="loginMfaIcon" class="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 mb-2">
              <svg class="w-7 h-7 sm:w-8 sm:h-8 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div id="loginMfaText">
              <h3 class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{{ mfaTitle }}</h3>
              <p class="text-slate-600 dark:text-slate-400 text-base sm:text-lg mt-2">
                {{ mfaDescription }}
              </p>
            </div>
          </div>

          <div id="otpInputBox" class="space-y-5 lg:space-y-6">
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wide text-center">
              {{ mfaInputLabel }}
            </label>
            <div id="loginMfaOtpInputs" class="flex justify-center space-x-2 sm:space-x-3" dir="ltr" @paste="onOtpPaste($event, 0)">
              <input
                v-for="i in 6"
                :key="`otp-${i-1}`"
                :id="`otp-${i-1}`"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                class="w-12 h-12 sm:w-14 sm:h-14 text-center text-lg sm:text-xl font-bold border-2 border-slate-200 dark:border-slate-700 rounded-lg sm:rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 shadow-sm"
                :value="otpDigits[i-1]"
                @input="onOtpInput(i-1, $event)"
                @keydown="onOtpKeydown(i-1, $event)"
                @paste="onOtpPaste($event, i-1)"
                :ref="el => setOtpRef(el, i-1)"
              />
            </div>
          </div>

          <div id="loginMfaError" v-if="errorMessage!==''" class="text-red-600 dark:text-red-400 text-sm font-medium text-center bg-red-50 dark:bg-red-900/20 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-red-200 dark:border-red-800">
            <div id="loginMfaErrorContent" class="flex items-center justify-center space-x-2">
              <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <span>{{ errorMessage }}</span>
            </div>
          </div>

          <button
            @click="submitMfaCode"
            :disabled="isOtpLoading === true"
            class="w-full bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold py-3 sm:py-4 px-6 rounded-lg sm:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
          >
            <Icon v-if="isOtpLoading === true" icon="svg-spinners:90-ring-with-bg" class="w-5 h-5 sm:w-6 sm:h-6" />
            <span>{{ mfaButtonLabel }}</span>
          </button>
        </div>
        </template>

        <div id="loginFooter" class="text-center pt-8 border-t border-slate-200 dark:border-slate-700">
          <p class="text-sm text-slate-500 dark:text-slate-400 font-medium">
            © {{ new Date().getFullYear() }} {{ brandName }}
          </p>
          <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
            {{ t('login.secureEnterprisePlatform', 'AI Agents Enterprise Platform') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@iconify/vue';
import { computed } from 'vue';
import { useLoginModel } from '@/Themes/shared/useLoginModel';
import { getActiveThemeKey, getRuntimeEnvValue } from '@/Themes/themeRuntime';
import ModernLogin from '@/Themes/modern/components/Login.vue';

const currentTheme = computed(() => getActiveThemeKey());

const {
  t,
  userName,
  password,
  organizationName,
  errorMessage,
  mfaStep,
  otpDigits,
  authAppSetupStep,
  authAppSecret,
  safeAuthAppQrCode,
  mfaTitle,
  mfaDescription,
  mfaInputLabel,
  mfaButtonLabel,
  isRTL,
  isLoading,
  isOtpLoading,
  login,
  submitMfaCode,
  returnToLogin,
  setOtpRef,
  onOtpInput,
  onOtpKeydown,
  onOtpPaste
} = useLoginModel();

const brandName = computed(() => {
  const whiteLabelName = getRuntimeEnvValue('VUE_APP_WHITELABEL_NAME');
  if (whiteLabelName !== null) {
    return whiteLabelName;
  }
  const companyName = getRuntimeEnvValue('VUE_APP_WHITE_LABEL_COMPANY_NAME');
  if (companyName !== null) {
    return companyName;
  }
  return t('login.defaultAppName', 'AI Agents');
});

function trimUserName() {
  if (typeof userName.value === 'string') {
    userName.value = userName.value.trim();
  }
}

function trimPassword() {
  if (typeof password.value === 'string') {
    password.value = password.value.trim();
  }
}

</script>

<style scoped>
:deep(.Vue-Toastification__container) {
  display: none !important;
}

#loginPage {
  min-height: 100vh;
  min-height: 100svh;
}

.enterprise-visual-panel {
  background-color: #0f172a;
  background-image:
    linear-gradient(rgba(100, 116, 139, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 116, 139, 0.1) 1px, transparent 1px),
    radial-gradient(circle at 86% 82%, rgba(14, 165, 233, 0.12), transparent 36%),
    radial-gradient(circle at 18% 14%, rgba(59, 130, 246, 0.08), transparent 34%),
    linear-gradient(145deg, #0f172a 0%, #111c30 52%, #0b1324 100%);
  background-size: 32px 32px, 32px 32px, auto, auto, auto;
}

.enterprise-visual-panel::after {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.52) 56%, rgba(15, 23, 42, 0.12) 100%);
  content: "";
  pointer-events: none;
}

#loginGlobe {
  position: absolute;
  right: -22%;
  bottom: -13%;
  z-index: 1;
  width: clamp(34rem, 54vw, 58rem);
  aspect-ratio: 1;
  opacity: 0.72;
  pointer-events: none;
  transform: rotate(-9deg);
}

[dir="rtl"] #loginGlobe {
  right: auto;
  left: -22%;
}

#loginGlobe svg {
  width: 100%;
  height: 100%;
  overflow: visible;
  transform-style: preserve-3d;
}

.globe-glow {
  fill: none;
  stroke: rgba(56, 189, 248, 0.18);
  stroke-width: 18;
  filter: url("#globeGlow");
}

.globe-grid {
  stroke-width: 1.15;
}

.globe-network {
  stroke: rgba(125, 211, 252, 0.35);
  stroke-width: 1.3;
}

.globe-points {
  fill: rgba(186, 230, 253, 0.72);
  stroke: rgba(56, 189, 248, 0.48);
  stroke-width: 5;
}

.globe-highlight {
  fill: none;
  stroke: rgba(224, 242, 254, 0.48);
  stroke-linecap: round;
  stroke-width: 2;
}

@media (max-width: 1024px) {
  #loginPage {
    flex-direction: column;
    min-height: 100vh;
    min-height: 100svh;
  }

  #loginPage > div:first-child {
    display: none;
  }
}

@media (max-width: 768px) {
  #loginPage > div:last-child > div {
    padding: 2rem 1.5rem;
    max-width: none;
  }
}

@media (max-width: 640px) {
  #loginPage {
    padding: 1rem;
  }

  #loginPage > div:last-child > div {
    padding: 1.5rem 1rem;
    max-width: none;
  }
}

input:focus,
button:focus {
  outline: 2px solid theme('colors.blue.500');
  outline-offset: 2px;
}

input,
button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

button:active {
  transform: translateY(1px);
}

.text-red-600 {
  animation: professionalShake 0.6s ease-in-out;
}

@keyframes professionalShake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
  transform: none !important;
}

::selection {
  background: theme('colors.blue.500');
  color: white;
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: theme('colors.slate.100');
}

::-webkit-scrollbar-thumb {
  background: theme('colors.slate.400');
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: theme('colors.slate.300');
}

.dark ::-webkit-scrollbar-track {
  background: theme('colors.slate.800');
}

.dark ::-webkit-scrollbar-thumb {
  background: theme('colors.slate.600');
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: theme('colors.slate.500');
}

input::placeholder {
  color: theme('colors.slate.400');
  opacity: 1;
}

.dark input::placeholder {
  color: theme('colors.slate.500');
}

input:hover {
  border-color: theme('colors.slate.300');
}

.dark input:hover {
  border-color: theme('colors.slate.600');
}

svg {
  transition: transform 0.2s ease-in-out;
}

button:hover svg {
  transform: translateX(1px);
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>